import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import type { ModalOptions } from '@ionic/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { RadioModalPage } from '@shared/pages';
import {
    AuthService, EmailBuilderService, PasswordService, UserAccountMapsService, UsersService
} from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import type { TEmail, TUser, TUserAccountMap } from '@shared/models';
import { PhoneMaskDirective } from '@shared/directives';

@Component({
    'selector': 'app-user',
    'templateUrl': './user.page.html',
    'styleUrls': ['./user.page.scss'],
    'standalone': true,
    'imports': [CommonModule, FormsModule, IonicModule, PhoneMaskDirective]
})
export class UserPage extends ViewBasePage implements OnInit, OnDestroy {
    public user: TUser;
    private uid = '';
    public error = {};
    public loggedInUser: any = {};
    public viewModel = {};
    public data = {};
    public saveAttempt = false;
    public submitted = false;
    public emailAddressIsReadonly = false;

    // /* private firebazeStorage: FirebazeStorageService, */
    constructor(private usersService: UsersService, private activatedRoute: ActivatedRoute,
        private userAccountMapsService: UserAccountMapsService, private authService: AuthService,
        private passwordService: PasswordService,
        private emailBuilderService: EmailBuilderService, injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_USER);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.user = this.usersService.getNullable();

        this.uid = this.activatedRoute.snapshot.paramMap.get('uid') ?? '';

        if (this.uid) {
            await this.uxHelperService.showSpinner();
            this.user = await this.usersService.getByUid(this.uid);
            this.user.cellPhone = this.utilsService.formatPhoneNumberForDisplay(this.user.cellPhone);

            // The user is not allowed to change the email address.  If a different email address is
            // required then they need to delete the original user then create a new user.
            this.emailAddressIsReadonly = true;
            await this.uxHelperService.hideSpinner();
        } else {
            this.user.role = 'serviceAdvisor';
        }
        this.initMoreMenu();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public initMoreMenu(): void {
        this.resetPopoverMenu();
        // this.pushPopoverMenuItem('Show Tour', 'show-user-tour');
    }

    public isReadonly() {
        const personWhoLoggedIn = this.sessionService.getCurrentUser();
        const shouldBeDisabled = (personWhoLoggedIn.role !== 'manager');
        return shouldBeDisabled;
    }

    private async saveNewUser() {
        this.user.email = this.user.email.toLowerCase();

        try {
            const isAvailable = await this.auth.isEmailAddressAvailable(this.user.email);
            if (!isAvailable) {
                await this.uxHelperService.showAlert('Sorry', 'This email address is already taken.');
                await this.uxHelperService.hideSpinner();
                return;
            }
            await this.uxHelperService.showSpinner('Saving.');

            // The logged in user, the manager in this case, is the one who is creating this new user.
            // Use the manager's account number for this new user.
            const profile = this.sessionService.getAccountProfile();
            const accountNumber = profile.accountNumber;

            // Generate a new password
            const password = await this.passwordService.generate();

            // Register this user so the can log in later.
            // As an aside, the registerUser method automatically logs in the user that is being created.
            // I don't like that.  If the manager is creating a driver, then the manager should
            // remain logged in even after the driver is created.
            const authData: any = await this.authService.registerUser(this.user.email, password);
            this.user.uid = authData.user.uid;

            const claims = {
                'accountNumber': accountNumber,
                'role': this.user.role,     // 'manager',
                'userUid': this.user.uid
            };
            await this.firebaseFunctionsService.setUserClaim(claims);
            await this.usersService.set(this.user, this.user.uid);

            const map: TUserAccountMap = {
                'uid': this.user.uid,
                'email': this.user.email,
                'accountNumber': accountNumber,
                'pwd': password,
                'createdDate': this.dateService.now()
            };
            await this.userAccountMapsService.set(map, this.user.uid);

            await this.sendEmail(this.user.email, password);

            await this.uxHelperService.hideSpinner();

            let msg = 'Success. A new user has been created. An email has been sent to: ';
            msg += this.user.email + '.  This email contains their login credentials.';

            // msg += 'Email = ' + this.user.email + ', Password = ' + password;
            // msg += '.  Tell this user the first thing they should do is go to the login screen ';
            // msg += 'and click Forgot Password to change their password.';
            await this.uxHelperService.showAlert('Important', msg);
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error:', errorMessage);
        }
    }

    //
    private async saveExistingUser() {
        try {
            await this.uxHelperService.showSpinner('Saving.');
            await this.usersService.update(this.user);
            this.user.cellPhone = this.utilsService.formatPhoneNumberForDisplay(this.user.cellPhone);
            await this.uxHelperService.hideSpinner();
            this.uxHelperService.showToast('Saved.');
            this.reloadSidemenuUser(this.user);
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error:', errorMessage);
        }
    }

    public async onSaveClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        try {
            if (!this.utilsService.validateEmailAddress(this.user.email)) {
                await this.uxHelperService.showAlert('Error', 'Email address is invalid.');
                return;
            }
            this.user.cellPhone = this.utilsService.unformatPhoneNumber(this.user.cellPhone);

            // this.firebazeStorage.saveUserProfilePicture('junk profile picture for now', this.user.uid);

            if (this.user.uid) {
                await this.saveExistingUser();
            } else {
                await this.saveNewUser();
            }
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            this.router.navigateByUrl('/managerSettings/users');
        }
    }

    // If the user that was just saved is the curent logged in user then
    // notify the app.component that it should reload the profile name.
    private reloadSidemenuUser(user) {
        if (user.uid === this.sessionService.getUsersUid()) {
            this.sessionService.setCurrentUser(user);
            this.eventsService.publish(this.Constants.EVENT_LOGGED_IN_USER_CHANGED, user.role);    // Handled by app.component.ts
        }
    }

    // public onRoleClick() {
    //     // If the logged in user is a service advisor or driver, dont allow them to change their role.
    //     // Only the manager can change their role.
    //     if (this.isReadonly()) {
    //         return;
    //     }

    //     const roleTitles = Roles.map(r => {
    //         return r.title;
    //     });
    //     this.uxHelperService.showRadioAlert('Select User\'s Role', roleTitles, this.user.roleTitle, (response) => {
    //         if (response.selection) {
    //             const arole = Roles.find(r => {
    //                 if (r.title === response.selection) { 
    //                     return r;
    //                 }
    //             });
    //             this.user.role = arole.role;
    //             this.user.roleTitle = arole.title;
    //         }
    //     });
    // }

    public async onRoleClick(): Promise<void> {
        // If the logged in user is a service advisor or driver, dont allow them to change their role.
        // Only the manager can change their role.
        if (this.isReadonly()) {
            return;
        }

        const roleTitles = Constants.Roles.map(r => r.title);

        const options: any = {
            'component': RadioModalPage,
            'componentProps': { 'items': roleTitles, 'selectedItem': this.user.roleTitle, 'title': 'Select User\'s Role' },
            'backdropDismiss': false,
            'showBackdrop': true,
            'cssClass': 'userRadioModal'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            const arole = Constants.Roles.find(r => r.title === response.data.selectedItem);
            if (arole) {
                this.user.role = arole.role;
                this.user.roleTitle = arole.title;
            } else {
                // Handle the case when no role is found.
                return; // Add a return statement here
            }
        }
    }

    private async sendEmail(emailAddress: string, password: string): Promise<void> {
        const url = `https://app.shuttlenomix.com`;
        const subject = `Re: ShuttleNomix Account Created`;

        const line1 = `Congratulations, your manager has created a ShuttleNomix account on your behalf.`;
        const line2 = `Go here to access the application: <a href="${url}">${url}</a>`;
        const line3 = `Click the Login button.`;
        const line4 = `Enter these login credentials:`;
        const line5 = `Email:    ${emailAddress}`;
        const line6 = `Password: ${password}`;
        const line7 = `Click the Login button.`;
        const line8 = `Your success coach will teach you and your team how to use ShuttleNomix.`;

        this.emailBuilderService.initializeEmailBody();
        this.emailBuilderService.setTitle(subject);
        this.emailBuilderService.addRow(line1);
        this.emailBuilderService.addRow(line2);
        this.emailBuilderService.addRow(line3);
        this.emailBuilderService.addRow(line4);
        this.emailBuilderService.addRow(line5);
        this.emailBuilderService.addRow(line6);
        this.emailBuilderService.addRow(line7);
        this.emailBuilderService.addRow(' ');
        this.emailBuilderService.addRow(line8);
        this.emailBuilderService.finalizeEmailBody();

        const body = this.emailBuilderService.getHtml();
        const email: TEmail = {
            'subject': subject,
            'to': emailAddress,
            'htmlBody': body
        }

        const response = await this.firebaseFunctionsService.sendEmail(email);
        return response;
    }
}
